import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import type { EndorsementError } from '../../shared/hooks';
import { useEndorsementForm, useEndorsementSubmit } from '../../shared/hooks';
import { useEndorsementFlow } from '../useEndorsementFlow';

export type { EndorsementError } from '../../shared/hooks';

type UseAddEndorsementProps = { petitionSlug: string };
type FormType = ReturnType<typeof useEndorsementForm>;
type FlowDataType = { isHelpModalOpen: boolean; isSubmitModalOpen: boolean };
type FlowActionsType = ReturnType<typeof useEndorsementFlow>['actions'];

function buildData(form: FormType, flowData: FlowDataType) {
	return {
		formData: form.formData,
		isSubmitting: form.isSubmitting,
		statementCharCount: form.statementCharCount,
		statementMaxLength: form.statementMaxLength,
		isHelpModalOpen: flowData.isHelpModalOpen,
		isSubmitModalOpen: flowData.isSubmitModalOpen,
		error: form.error,
	};
}

type BuildActionsParams = {
	form: FormType;
	flowActions: FlowActionsType;
	handlers: { handleSubmit: () => void; handleNext: () => void; confirmSubmit: () => Promise<void> };
};

function buildActions({ form, flowActions, handlers }: BuildActionsParams) {
	return {
		handleFieldChange: form.handleFieldChange,
		handlePhotoUpload: form.handlePhotoUpload,
		handleSubmit: handlers.handleSubmit,
		handleNext: handlers.handleNext,
		handleBack: flowActions.handleBack,
		handleHelpClick: flowActions.handleHelpClick,
		openHelpModal: flowActions.openHelpModal,
		closeHelpModal: flowActions.closeHelpModal,
		openSubmitModal: flowActions.openSubmitModal,
		closeSubmitModal: flowActions.closeSubmitModal,
		confirmSubmit: handlers.confirmSubmit,
		clearError: form.clearError,
	};
}

export function useAddEndorsement({ petitionSlug }: UseAddEndorsementProps) {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const form = useEndorsementForm();
	const { data: flowData, actions: flowActions } = useEndorsementFlow({ petitionSlug });

	const onSuccess = useCallback(() => {
		flowActions.closeSubmitModal();
		flowActions.navigateToEditPage();
	}, [flowActions]);

	const onError = useCallback(
		(error: EndorsementError) => {
			form.setError(error);
			flowActions.closeSubmitModal();
		},
		[form, flowActions],
	);

	const { submit: confirmSubmit } = useEndorsementSubmit({
		petitionId: petition.id,
		formData: form.formData,
		utilityContext,
		track,
		onSuccess,
		onError,
		setIsSubmitting: form.setIsSubmitting,
	});

	const handleSubmit = useCallback(() => {
		void track('starter_dashboard_endorsement_submit_click');
		flowActions.openSubmitModal();
	}, [track, flowActions]);
	const handleNext = useCallback(() => {
		void track('starter_dashboard_endorsement_next_click');
		flowActions.openSubmitModal();
	}, [track, flowActions]);

	return {
		data: buildData(form, flowData),
		actions: buildActions({ form, flowActions, handlers: { handleSubmit, handleNext, confirmSubmit } }),
	};
}
